import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzInputModule } from 'ng-zorro-antd/input';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
} from 'ng-zorro-antd/form';
import {
  ProgressReportGraphMapping,
  reportDrawerParams,
  ReportRes,
} from '../report-manager.interface';
import { CommonModule } from '@angular/common';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { GraphList } from '../../../insights-data/insights-data.interface';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { DrawerService } from '../../../../../../core/services/drawer.service';
import { DRAWER_IDS } from '../../../../../../core/constants/drawer-ids.constant';
import { CaseDetailModel } from '../../../../insights.interface';
import { InsightMode } from '../../../../insights.enums';
import { FrequencyDatasheetGraphComponent } from '../../../insights-data/data-graph/frequency-datasheet-graph/frequency-datasheet-graph.component';
import { LatencyGraphComponent } from '../../../insights-data/data-graph/latency-graph/latency-graph.component';
import { PartialIntervalGraphComponent } from '../../../insights-data/data-graph/partial-interval-graph/partial-interval-graph.component';
import { ReplacementBehaviorGraphComponent } from '../../../insights-data/data-graph/replacement-behavior-graph/replacement-behavior-graph.component';
import { FrequencyIntervalGraphComponent } from '../../../insights-data/data-graph/frequency-interval-graph/frequency-interval-graph.component';
import { NoDataComponent } from '../../../../../../pages/content/no-data/no-data.component';
@Component({
  selector: 'app-report-graph',
  standalone: true,
  imports: [
    NzDividerModule,
    NzInputModule,
    NzFormItemComponent,
    NzFormControlComponent,
    NzFormLabelComponent,
    CommonModule,
    ReactiveFormsModule,
    FrequencyDatasheetGraphComponent,
    LatencyGraphComponent,
    PartialIntervalGraphComponent,
    ReplacementBehaviorGraphComponent,
    FrequencyIntervalGraphComponent,
    NoDataComponent,
  ],
  templateUrl: './report-graph.component.html',
  styleUrl: './report-graph.component.scss',
})
export class ReportGraphComponent implements OnInit {
  @Input() graphData: ReportRes | null = null;
  @Output() formValueChange = new EventEmitter<ProgressReportGraphMapping[]>();
  graphForm!: FormGroup;
  private readonly destroy$ = new Subject<void>();
  reportData: ReportRes | null | undefined = null;
  caseParams: reportDrawerParams = {
    case: {} as CaseDetailModel,
    mode: InsightMode.PRINT_CASE,
    selectedReport: null,
  };
  constructor(
    private fb: FormBuilder,
    private drawerService: DrawerService
  ) {
    this.setupDrawerSubscription();
  }

  ngOnInit(): void {
    this.graphForm = this.fb.group({
      graphs: this.fb.array([]),
    });
    if (this.graphData && this.graphData.graphList) {
      this.graphData.graphList.forEach(graph => {
        this.graphs.push(this.createGraphFormGroup(graph));
      });
    }
    this.formValueChange.emit(this.graphForm.value.graphs);

    // Subscribe to form value changes, debounce, and emit the updated value
    this.graphForm.valueChanges.pipe(debounceTime(1000)).subscribe(value => {
      this.formValueChange.emit(value.graphs);
    });
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<reportDrawerParams>(DRAWER_IDS.CREATE_REPORT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.caseParams = params;
          this.reportData = params.selectedReport;
        }
      });
  }

  get graphs(): FormArray {
    return this.graphForm.get('graphs') as FormArray;
  }
  get graphControls(): FormGroup[] {
    return this.graphs?.controls as FormGroup[];
  }

  getGraphData(index: number) {
    return this.graphData && this.graphData.graphList
      ? this.graphData?.graphList[index]
      : null;
  }

  hasFrequencyData(index: number): boolean {
    const graphData = this.getGraphData(index);
    if (!graphData?.intervalData?.frequencyData) {
      return false;
    }
    return Object.keys(graphData.intervalData.frequencyData).length > 0;
  }

  createGraphFormGroup(graph: GraphList): FormGroup {
    const progressMapping = this.reportData?.progressReportGraphMappings?.find(
      mapping => mapping.activityDataUnitId === graph.id
    );

    return this.fb.group({
      id: [progressMapping?.id || 0],
      activityDataUnitId: [graph.id, Validators.required],
      comment: [progressMapping?.comment || '', Validators.required],
    });
  }
}
